/**
 * deviceId.js
 * ────────────
 * Persistent per-browser device identifier used by the session-management feature.
 *
 * - Stored in localStorage as `device_id` (UUID v4 string).
 * - Generated lazily on first access.
 * - Sent on EVERY authed fetch via the `X-Device-Id` header so the backend can:
 *     1. Recognise the same device across sign-outs / re-sign-ins (no double-counting).
 *     2. Embed the matching session id in the issued JWT (sid claim).
 *
 * On a successful login the backend returns its own canonical `device_id` (which may
 * differ from what we sent if it created a fresh session). Call `setDeviceId()` with
 * that returned value to stay in sync.
 */

const KEY = 'device_id';

function uuidv4() {
  // RFC4122-compliant UUID — works in all modern browsers without a dep
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getDeviceId() {
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = uuidv4();
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch (e) {
    // localStorage blocked (private mode, security setting) — fall back to a session-scoped id
    if (!window.__sessionDeviceId) window.__sessionDeviceId = uuidv4();
    return window.__sessionDeviceId;
  }
}

export function setDeviceId(id) {
  try {
    if (id) localStorage.setItem(KEY, id);
  } catch (e) {
    if (id) window.__sessionDeviceId = id;
  }
}

export function clearDeviceId() {
  try {
    localStorage.removeItem(KEY);
  } catch (e) {
    delete window.__sessionDeviceId;
  }
}

/**
 * Returns headers object with X-Device-Id set, ready to merge into fetch options.
 *   fetch(url, { headers: { ...authHeaders(), ...deviceHeader() } })
 */
export function deviceHeader() {
  return { 'X-Device-Id': getDeviceId() };
}
